import { Field, Input } from 'components';
import QuestionOrganism from 'questionFlow/QuestionOrganism';
import { useCallback, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { ValidationRule } from '../types';
import {
  DefaultIncomeQuestionComponentProps,
  DefaultIncomeValuesType,
  StepNamesDefaultIncome,
} from '../flows/defaultIncome/types';
import DateInput, { OnDateChange } from 'components/molecules/DateInput/DateInput';
import { DateValue, isDateValue } from './YourSalary/types';
import { useAnalyticsErrors } from 'monitoring/analyticsHooks';

const cmsLocation = '/questions/income/pensionDetails';

const useContent = (t: TFunction<'translation'>) => ({
  title: t(`${cmsLocation}.title`),
  subtitle: t(`${cmsLocation}.subtitle`),
  pensionProvider: {
    label: t(`${cmsLocation}.pensionProvider.label`),
    placeholder: t(`${cmsLocation}.pensionProvider.placeholder`),
  },
  startDate: {
    label: t(`${cmsLocation}.startDate.label`),
    dayInputLabel: t(`${cmsLocation}.startDate.dayInputLabel`),
    monthInputLabel: t(`${cmsLocation}.startDate.monthInputLabel`),
    yearInputLabel: t(`${cmsLocation}.startDate.yearInputLabel`),
  },
});

export type PensionDetailsQuestionName = 'startDate' | 'pensionProvider';

export type PensionDetailsValues = {
  pensionProvider: string;
  startDate: DateValue;
};

export const useValues = (initialValues?: DefaultIncomeValuesType) => {
  const currentDetails = initialValues?.pensionDetails as
    | { pensionProvider: string; startDate: string }
    | undefined;

  return useState<PensionDetailsValues>({
    startDate: currentDetails?.startDate
      ? {
          value: currentDetails.startDate,
          valid: true,
        }
      : {
          value: '',
          valid: false,
        },
    pensionProvider: currentDetails?.pensionProvider || '',
  });
};

export const useErrors = () =>
  useAnalyticsErrors<Record<PensionDetailsQuestionName, string>>({
    startDate: '',
    pensionProvider: '',
  });

const validate = (v: string, rules: ValidationRule[], valid?: boolean) =>
  rules.find(({ rule }) => !rule(v, valid));

function assertIsCompanyDetailsQuestionName(
  maybe: string,
): asserts maybe is PensionDetailsQuestionName {
  if (maybe === 'pensionProvider') {
    return;
  }

  throw new Error(maybe + ' is not a known PensionDetailsQuestionName');
}

const stepName = StepNamesDefaultIncome.pensionDetails;

const PensionDetails = ({
  data,
  onSubmit,
  values: initialValues,
  validationRules: validationRulesGeneric,
  ...props
}: DefaultIncomeQuestionComponentProps) => {
  const validationRules = (validationRulesGeneric as any) as Record<
    PensionDetailsQuestionName,
    ValidationRule[]
  >;
  const { t } = useTranslation();
  const content = useContent(t);
  const [values, setValues] = useValues(initialValues);
  const [error, setError] = useErrors();

  const getValidationResult = useCallback(
    (name: PensionDetailsQuestionName, givenValue?: string) => {
      const actualValue = givenValue === undefined ? values[name] || '' : givenValue;

      return isDateValue(actualValue)
        ? validate(actualValue.value, validationRules[name], actualValue.valid)
        : validate(actualValue, validationRules[name]);
    },
    [validationRules, values],
  );

  const adaptToExternalFormat = ({
    startDate,
    ...input
  }: PensionDetailsValues): Record<PensionDetailsQuestionName, string> => ({
    ...input,
    startDate: startDate.value,
  });

  const handleSubmit = useCallback(() => {
    const errors = (Object.keys(values) as PensionDetailsQuestionName[]).reduce((e, inputName) => {
      const result = getValidationResult(inputName);

      return { ...e, [inputName]: result ? t(result.error as any) : '' };
    }, error);

    if (Object.values(errors).filter(e => e !== '').length === 0) {
      onSubmit({ key: stepName, value: adaptToExternalFormat(values) });
    } else {
      setError(errors);
    }
  }, [error, getValidationResult, onSubmit, setError, t, values]);

  const handleDateOnBlur = (name: PensionDetailsQuestionName) => {
    const result = getValidationResult(name);
    setError({ ...error, [name]: result ? t(result.error as any) : '' });
  };

  const handleDateChange: OnDateChange = ({ name, value, valid }) => {
    setValues({
      ...values,
      [name]: {
        value,
        valid,
      },
    });

    if (valid && error[name as PensionDetailsQuestionName]) {
      setError({ ...error, [name]: '' });
    }
  };

  const validateAndSetError = useCallback(
    (name: PensionDetailsQuestionName, newValue: string) => {
      const result = getValidationResult(name, newValue);
      setError({ ...error, [name]: result ? t(result.error as any) : '' });
    },
    [error, getValidationResult, setError, t],
  );

  const handleInputOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const name = e.target.name as PensionDetailsQuestionName;

      assertIsCompanyDetailsQuestionName(name);

      if (error[name]) {
        validateAndSetError(name, value.trim());
      }

      setValues({ ...values, [name]: value.trim() });
    },
    [error, setValues, validateAndSetError, values],
  );

  const handleInputOnBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const name = e.target.name as PensionDetailsQuestionName;

      assertIsCompanyDetailsQuestionName(name);

      validateAndSetError(name, e.target.value.trim());
    },
    [validateAndSetError],
  );

  return (
    <QuestionOrganism
      id={stepName}
      data-testid={stepName}
      onSubmit={handleSubmit}
      title={content.title}
      subtitle={content.subtitle}
      {...props}
    >
      <Field
        data-testid={`${stepName}-pensionProvider`}
        label={content.pensionProvider.label}
        validationError={error.pensionProvider}
      >
        <Input
          id={`${stepName}-pensionProvider`}
          name={'pensionProvider'}
          defaultValue={values.pensionProvider}
          onChange={handleInputOnChange}
          aria-invalid={!!error.pensionProvider}
          onBlur={handleInputOnBlur}
          placeholder={content.pensionProvider.placeholder}
        />
      </Field>

      <Field
        data-testid={`${stepName}-startDate`}
        label={content.startDate.label}
        validationError={error.startDate}
      >
        <DateInput
          data-testid="start-date-input"
          onChange={handleDateChange}
          onBlur={() => handleDateOnBlur('startDate')}
          defaultValue={values.startDate.value}
          error={!!error.startDate}
          id="startDate"
          dayLabel={content.startDate.dayInputLabel}
          monthLabel={content.startDate.monthInputLabel}
          yearLabel={content.startDate.yearInputLabel}
        />
      </Field>
    </QuestionOrganism>
  );
};

export default PensionDetails;
